import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Boxes, Download, Minus, Receipt, RotateCcw, TrendingUp, Users, Wallet } from "lucide-react";

import { getReconciliation } from "src/api";
import { getInventory } from "src/api/inventory";
import env from "src/config/env";
import { useAuth } from "src/providers/AuthProvider";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import ComboSearch from "src/components/ComboSearch";
import CustomDateRange from "src/components/CustomDateRange";
import { Button } from "src/components/ui/button";
import { BranchSelect, PageHead, StatCard } from "src/components/common";
import { getCustomizationValue, getFromLocalStorage, renderCurrency, renderNumber } from "src/lib/utils";

const toNumber = (value?: number | string) => Number(value ?? 0) || 0;

const Reconciliation = () => {
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	const entity = searchParams.get("entity") ?? "";
	const product = searchParams.get("product") ?? "";
	const startDate = searchParams.get("start_date");
	const endDate = searchParams.get("end_date");

	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";
	const customerLabelPlural = getCustomizationValue(user, "customer_label") || "Customers";
	const inventoryLabel = getCustomizationValue(user, "inventory_label", true) || "Inventory";

	const { data } = useQuery({
		queryKey: ["reconciliation", entity, startDate, endDate, product],
		queryFn: () => getReconciliation({ ...(entity && { entity }), start_date: startDate, end_date: endDate, product }),
		suspense: true,
	});

	const stats = data?.data;

	const updateSearchParams = (key: string, value: string) => {
		const next = new URLSearchParams(searchParams);
		if (value === "0" || !value) next.delete(key);
		else next.set(key, value);
		setSearchParams(next);
	};

	const { mutate: exportData, isLoading: isExporting } = useMutation({
		mutationFn: async () => {
			const accessToken = getFromLocalStorage<string>("access_token");

			return await axios.get(`${env.REACT_APP_API_BASE_URL}/reconciliation/export`, {
				headers: { Authorization: `Bearer ${accessToken}` },
				responseType: "blob",
			});
		},
		onSuccess: (response) => {
			const blob = new Blob([response.data]);
			const url = window.URL.createObjectURL(blob);

			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", "reconciliation_data.xlsx");

			document.body.appendChild(link);
			link.click();
			link.remove();

			window.URL.revokeObjectURL(url);
		},
	});

	const revenue = toNumber(stats?.total_revenue);
	const cost = toNumber(stats?.sales_cost);
	const profit = toNumber(stats?.gross_profit);
	const marginPct = revenue > 0 ? (profit / revenue) * 100 : 0;

	return (
		<div className="tw-space-y-5">
			<PageHead
				title="Reconciliation"
				subtitle="Revenue, cost of sales and outstanding balances for the selected period."
				actions={
					<Button size="sm" variant="outline" className="tw-gap-1.5" onClick={() => exportData()} disabled={isExporting}>
						<Download className="tw-h-4 tw-w-4" />
						{isExporting ? "Exporting..." : "Export data"}
					</Button>
				}
			/>

			<div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
				<BranchSelect value={entity} onChange={(value) => updateSearchParams("entity", value)} allLabel="All stores" />

				<div className="tw-w-full sm:tw-w-[260px]">
					<ComboSearch
						value={product}
						onChange={(value: string) => updateSearchParams("product", value)}
						placeholder="Search product..."
						className="tw-h-10 tw-w-full tw-justify-between tw-rounded-[10px] tw-border-border tw-text-[13.5px]"
						optionRenderer={({ name, sku }: { name?: string; sku?: string }) => (
							<p>
								{name} (SKU: {sku})
							</p>
						)}
						queryKey="items"
						queryFN={getInventory}
						mapFN={(option) => ({
							label: option.name,
							value: String(option.id ?? ""),
							info: option,
						})}
					/>
				</div>

				<CustomDateRange />

				<Button size="sm" variant="outline" className="tw-gap-1.5" onClick={() => setSearchParams({})}>
					<RotateCcw className="tw-h-4 tw-w-4" />
					Clear
				</Button>
			</div>

			{/*
				Prototype's waterfall: Total Revenue − Cost of Sales = Gross Profit.
				All three figures are real here — the API already computes them (spec §3.2).
			*/}
			<div className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-5 tw-shadow-card">
				<div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
					<h3 className="tw-text-[15px] tw-font-bold tw-text-text">Gross Profit</h3>
					<span
						className={`tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[12px] tw-font-bold ${
							marginPct >= 0 ? "tw-bg-teal-tint tw-text-teal-dark" : "tw-bg-red-tint tw-text-red"
						}`}
					>
						{marginPct.toFixed(1)}% margin
					</span>
				</div>

				<div className="tw-flex tw-flex-wrap tw-items-stretch tw-gap-3">
					<WaterfallStep label="Total Revenue" value={renderCurrency(revenue)} tone="teal" />
					<WaterfallOperator icon={<Minus className="tw-h-4 tw-w-4" />} />
					<WaterfallStep label="Cost of Sales" value={renderCurrency(cost)} tone="amber" />
					<WaterfallOperator icon={<span className="tw-text-[15px] tw-font-bold">=</span>} />
					<WaterfallStep label="Gross Profit" value={renderCurrency(profit)} tone="navy" emphasis />
				</div>
			</div>

			<div className="tw-grid tw-gap-4 tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
				<StatCard
					label="Total Number of Transactions"
					value={renderNumber(stats?.total_sales_count ?? 0)}
					icon={Receipt}
					tone="blue"
				/>
				<StatCard
					label={`Avg. Order Per ${customerLabel}`}
					value={renderCurrency(stats?.avg_customer_order ?? 0)}
					icon={TrendingUp}
					tone="teal"
				/>
				<StatCard
					label={`Debts Owed by ${customerLabelPlural}`}
					value={renderCurrency(stats?.debt_owed ?? 0)}
					icon={Users}
					tone="red"
				/>
				<StatCard
					label={`${inventoryLabel} Worth (Selling Price)`}
					value={renderCurrency(stats?.inventory_selling_price_worth ?? 0)}
					icon={Boxes}
					tone="navy"
				/>
				<StatCard
					label={`${inventoryLabel} Worth (Cost Price)`}
					value={renderCurrency(stats?.inventory_cost_price_worth ?? 0)}
					icon={Wallet}
					tone="slate"
				/>
			</div>
		</div>
	);
};

const toneClasses = {
	teal: "tw-border-teal/25 tw-bg-teal-tint",
	amber: "tw-border-amber/25 tw-bg-amber-tint",
	navy: "tw-border-navy/20 tw-bg-navy-tint",
} as const;

const WaterfallStep = ({
	label,
	value,
	tone,
	emphasis,
}: {
	label: string;
	value: string;
	tone: keyof typeof toneClasses;
	emphasis?: boolean;
}) => (
	<div className={`tw-min-w-[150px] tw-flex-1 tw-rounded-[10px] tw-border tw-px-4 tw-py-3.5 ${toneClasses[tone]}`}>
		<div className="tw-mb-1.5 tw-text-[12.5px] tw-font-medium tw-text-text-soft">{label}</div>
		<div className={`tw-font-bold tw-text-text ${emphasis ? "tw-text-[22px]" : "tw-text-[18px]"}`}>{value}</div>
	</div>
);

const WaterfallOperator = ({ icon }: { icon: React.ReactNode }) => (
	<div className="tw-flex tw-items-center tw-justify-center tw-text-text-soft">{icon}</div>
);

export default withPermissionWrapper(Reconciliation, "view_reconciliation");
